import React, { FC, useCallback, useState, PropsWithChildren, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { QueryParamsContext } from 'src/contexts'

export interface DefaultsEntry {
  queryValues: Record<string, string>;
  when?: (params: Record<string, string>, pathname: string) => boolean;
}

interface QueryParamsProviderProps extends PropsWithChildren {
  defaults?: DefaultsEntry[];
}

function getActiveDefaults(
  params: Record<string, string>,
  defaults: DefaultsEntry[],
  pathname: string,
): Record<string, string> {
  return defaults.reduce((acc, entry) => {
    if (!entry.when || entry.when(params, pathname)) {
      return { ...acc, ...entry.queryValues }
    }
    return acc
  }, {} as Record<string, string>)
}

export const QueryParamsProvider: FC<QueryParamsProviderProps> = ({ children, defaults = [] }) => {
  const navigate = useNavigate()
  const location = useLocation()

  const initial = Array.from(
    new URLSearchParams(location.search).entries()
  ).reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {} as Record<string, string>)
  const [state, setState] = useState<Record<string, string>>(() => ({
    ...getActiveDefaults(initial, defaults, location.pathname),
    ...initial,
  }))

  useEffect(() => {
    setState((prev) => {
      const keysToKeep = new Set(defaults.flatMap(entry => {
        if (!entry.when || entry.when(prev, location.pathname)) {
          return Object.keys(entry.queryValues)
        }
        return []
      }))
      const keysToRemove = defaults.flatMap(entry => {
        if (entry.when && !entry.when(prev, location.pathname)) {
          return Object.keys(entry.queryValues).filter(key => !keysToKeep.has(key))
        }
        return []
      })

      if (keysToRemove.length === 0) return prev

      const newState = { ...prev }
      for (const key of keysToRemove) {
        delete newState[key]
      }
      return newState
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  useEffect(() => {
    const searchParams = Object.keys(state)
      .sort()
      .reduce(
        (acc, key) => ({
          ...acc,
          [key]: state[key],
        }),
        {},
      )
    const params = new URLSearchParams(searchParams).toString()
    const { pathname } = location
    const to = `${pathname}${params.length ? `?${params}` : ''}`
    navigate(to, { replace: true })
  }, [state]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateState = useCallback(
    async (newState: Record<string, string>, paramsToRemove: string[] = []): Promise<void> => {
      setState((prev) => {
        const prevState = { ...prev }
        for (const param of paramsToRemove) delete prevState[param]
        const merged = { ...prevState, ...newState }
        for (const key of Object.keys(merged)) {
          if (merged[key] === '') delete merged[key]
        }
        return { ...getActiveDefaults(merged, defaults, location.pathname), ...merged }
      })
    },
    [defaults, location.pathname]
  )

  const remove = useCallback(
    async (paramsToRemove?: string[]) => {
      if (!paramsToRemove || paramsToRemove.length === 0) return
      await updateState({}, paramsToRemove)
    },
    [updateState]
  )

  const resetState = useCallback(async (): Promise<void> => new Promise((resolve) => {
    setState(getActiveDefaults({}, defaults, location.pathname))
    resolve()
  }), [defaults, location.pathname])

  return (
    <QueryParamsContext.Provider value={[state, updateState, remove, resetState]}>
      {children}
    </QueryParamsContext.Provider>
  )
}
